import { getSummativeCompetency } from './summativeCompetency';

const OUTCOME_ORDER = [
  'competent',
  'retake_one',
  'not_yet_competent',
  'academic_leave',
  'absconded',
  'deregistration',
  'defer',
  'special_exam',
  'withhold',
];

// TEMPORARY VERIFICATION DATA: remove this block and set the flag to false
// once live award candidates are available in the approved results.
const ENABLE_DEMO_AWARD_DATA = true;
const DEMO_AWARD_PROGRAMMES = [
  { level: 3, name: 'Building and Construction' },
  { level: 3, name: 'Beauty Therapy' },
  { level: 3, name: 'Plumbing Technology' },
  { level: 4, name: 'Automotive Engineering' },
  { level: 4, name: 'Electrical Installation' },
  { level: 4, name: 'Catering and Hospitality' },
  { level: 5, name: 'Welding and Fabrication' },
  { level: 5, name: 'Fashion Design' },
  { level: 5, name: 'Information Communication Technology' },
  { level: 6, name: 'Repair and Maintenance of Mobile and Portable Devices' },
  { level: 6, name: 'Hairdressing' },
  { level: 6, name: 'Motor Vehicle Technology' },
];
const DEMO_AWARD_CATEGORIES = ['ATTAINED MASTERY', 'PROFICIENT', 'COMPETENT'];
const DEMO_AWARD_CANDIDATES = Array.from({ length: 50 }, (_, index) => {
  const programme = DEMO_AWARD_PROGRAMMES[index % DEMO_AWARD_PROGRAMMES.length];
  const category = DEMO_AWARD_CATEGORIES[index % DEMO_AWARD_CATEGORIES.length];
  const serial = String(index + 1).padStart(3, '0');
  return {
    level: programme.level,
    programme: programme.name,
    category,
    registrationNumber: `DEMO${programme.level}/${serial}/2026`,
    name: `DEMO CANDIDATE ${String(index + 1).padStart(2, '0')}`,
  };
});

export const senateOutcomeLabels = {
  competent: 'Competent',
  retake_one: 'Retake One',
  not_yet_competent: 'Not Yet Competent',
  academic_leave: 'Academic Leave',
  absconded: 'Abscondment',
  deregistration: 'Deregistration',
  defer: 'Defer',
  special_exam: 'Special Exam',
  withhold: 'Withhold',
};

const text = value => String(value || '').trim();

const explicitOutcome = (marks, formalStatus = '') => {
  // A missing summative mark is not, by itself, abscondment.  A learner who
  // has formative work recorded (or an explicitly recorded competency
  // outcome such as NYC-F) participated in assessment and must be reported
  // under Retake One / Not Yet Competent instead.  Only learners with no
  // assessment evidence at all should be classified as absconded.
  const hasAssessmentEvidence = marks.some(mark => {
    if (!mark) return false;
    const formative = Number(mark?.formative_total_60);
    const recordedText = [
      mark?.remarks,
      mark?.rating,
      mark?.grade_letter,
      mark?.status,
      mark?.outcome,
      mark?.reason,
      mark?.competency,
    ].map(text).join(' ').toLowerCase();
    return mark.raw_marks != null
      || mark.converted_marks != null
      || (Number.isFinite(formative) && formative > 0)
      || /\b(?:nyc(?:-[a-z]+)?|not\s+yet\s+competent|competent|proficient|mastery)\b/.test(recordedText);
  });
  const remarks = marks.map(mark => [
    text(mark?.remarks),
    text(mark?.rating),
    text(mark?.grade_letter),
    text(mark?.status),
    text(mark?.outcome),
    text(mark?.reason),
  ].join(' ')).concat(formalStatus).join(' ').toLowerCase();
  const hasIrregularityCode = marks.some(mark => ['EI', 'IRREGULARITY', 'EXAM_IRREGULARITY'].includes(text(mark?.grade_letter).toUpperCase()) || ['EI', 'IRREGULARITY', 'EXAM_IRREGULARITY'].includes(text(mark?.status).toUpperCase()));
  // An examination irregularity or disciplinary case takes precedence over a
  // passing mark (and over a missing mark). The Senate outcome is withheld
  // until the case is resolved.
  if (hasIrregularityCode || /withhold|disciplin|irregular/.test(remarks)) return 'withhold';
  if (/\bdefer(?:red|ment)?\b/.test(remarks)) return 'defer';
  if (marks.some(mark => !mark || mark?.raw_marks == null) && !hasAssessmentEvidence) return 'absconded';
  if (/academic\s+leave/.test(remarks)) return 'academic_leave';
  if (/abscond/.test(remarks) && !hasAssessmentEvidence) return 'absconded';
  if (/deregister/.test(remarks)) return 'deregistration';
  if (/special\s+exam/.test(remarks)) return 'special_exam';
  return null;
};

export const classifySenateStudent = student => {
  const marks = [...(student?.marks?.values?.() || [])];
  const formalStatus = [student?.status, student?.enrollmentStatus, student?.enrollment_status]
    .map(value => text(value))
    .filter(Boolean)
    .join(' ');
  const explicit = explicitOutcome(marks, formalStatus);
  const abscondmentCount = Number(student?.abscondmentCount || 0);
  const failures = marks.map(mark => ({ mark, result: getSummativeCompetency(mark) }))
    .filter(({ result }) => String(result.remark || '').toUpperCase().startsWith('NYC'));
  const key = explicit === 'absconded' && abscondmentCount > 2
    ? 'deregistration'
    : explicit || (failures.length === 0 ? 'competent' : failures.length === 1 ? 'retake_one' : 'not_yet_competent');
  const failureIssues = failures.map(({ mark, result }) => {
    const unit = text(mark?.unit_code) || 'Unit';
    const reason = text(mark?.remarks) || result.remark || 'Not Yet Competent';
    return `${unit}: ${reason}`;
  });
  const explicitIssues = explicit ? marks.flatMap(mark => {
    const recorded = text(mark?.remarks) || text(mark?.rating) || text(mark?.grade_letter) || text(mark?.status) || text(mark?.reason);
    if (!mark || mark?.raw_marks == null) {
      const unit = text(mark?.unit_code) || 'Unit';
      return [`${unit}: ${recorded || 'Absconded / incomplete examination'}`];
    }
    if (!recorded) return [];
    const unit = text(mark?.unit_code) || 'Unit';
    return [`${unit}: ${recorded}`];
  }) : [];
  const statusIssues = explicit === 'defer' && !marks.length ? ['Status: Formally deferred'] : [];
  const issues = [...new Set([...failureIssues, ...explicitIssues, ...statusIssues])];
  if (key === 'deregistration') issues.unshift(`Absconded ${abscondmentCount} times; deregistration recommended`);
  const ratings = marks.map(mark => text(mark?.rating).toLowerCase());
  const award = key === 'competent'
    ? (ratings.some(value => /mastery/.test(value)) ? 'Mastery' : ratings.some(value => /proficient/.test(value)) ? 'Proficient' : 'Competent')
    : null;
  return {
    key,
    label: senateOutcomeLabels[key] || 'Not Yet Competent',
    issues,
    award,
  };
};

export const senateCandidateRows = group => (group?.students || []).map(student => ({
  ...student,
  senateOutcome: classifySenateStudent(student),
}));

export const senateGroupSummary = group => {
  const counts = Object.fromEntries(OUTCOME_ORDER.map(key => [key, 0]));
  senateCandidateRows(group).forEach(row => { counts[row.senateOutcome.key] = (counts[row.senateOutcome.key] || 0) + 1; });
  return counts;
};

const drawWrapped = (pdf, value, x, y, maxWidth, lineHeight = 4) => {
  const lines = pdf.splitTextToSize(text(value) || '—', maxWidth);
  pdf.text(lines, x, y);
  return y + (lines.length * lineHeight);
};

const drawRichWrapped = (pdf, segments, x, y, maxWidth, lineHeight = 5) => {
  let cursorX = x;
  let cursorY = y;
  let pendingInlineSpace = false;
  segments.forEach(segment => {
    if (segment.noSplit) {
      pdf.setFont('times', segment.bold ? 'bold' : 'normal');
      const width = pdf.getTextWidth(String(segment.text || ''));
      if (cursorX > x && cursorX + width > x + maxWidth) {
        cursorX = x;
        cursorY += lineHeight;
      }
      pdf.text(String(segment.text || ''), cursorX, cursorY);
      // Mark the boundary so the following segment receives a real space
      // text object; this prevents phrases such as “CERTIFICATEin”.
      cursorX += width;
      pendingInlineSpace = true;
      return;
    }
    // Emit a real space text object after a bold no-split phrase. This is
    // stronger than relying on adjacent text coordinates and keeps extraction
    // from joining words such as “CERTIFICATEin”. Strip any authored leading
    // whitespace so the gap is emitted exactly once.
    let segmentText = String(segment.text || '');
    if (pendingInlineSpace && segmentText.trim()) {
      const gap = Math.max(pdf.getTextWidth(' '), 2.2);
      if (cursorX > x && cursorX + gap > x + maxWidth) {
        cursorX = x;
        cursorY += lineHeight;
      } else if (cursorX > x) {
        pdf.setFont('times', 'normal');
        pdf.text(' ', cursorX, cursorY);
        cursorX += gap;
      }
      segmentText = segmentText.replace(/^\s+/, '');
      pendingInlineSpace = false;
    }
    const tokens = segmentText.split(/(\s+)/).filter(Boolean);
    tokens.forEach(token => {
      pdf.setFont('times', segment.bold ? 'bold' : 'normal');
      const width = pdf.getTextWidth(token);
      const whitespace = /^\s+$/.test(token);
      if (!whitespace && cursorX > x && cursorX + width > x + maxWidth) {
        cursorX = x;
        cursorY += lineHeight;
      }
      if (whitespace) {
        if (cursorX !== x) cursorX += Math.max(width, pdf.getTextWidth(' '));
        return;
      }
      pdf.text(token, cursorX, cursorY);
      cursorX += width;
    });
  });
  return cursorY + lineHeight;
};

const safeFilePart = value => text(value || 'package').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();

const ordinalLabel = value => {
  const raw = text(value);
  const number = Number.parseInt(raw, 10);
  if (!Number.isFinite(number)) return raw.toUpperCase();
  const mod100 = number % 100;
  const suffix = mod100 >= 11 && mod100 <= 13 ? 'TH' : ({ 1: 'ST', 2: 'ND', 3: 'RD' }[number % 10] || 'TH');
  return `${number}${suffix}`;
};

const numberToWords = value => {
  const number = Math.max(0, Math.floor(Number(value) || 0));
  const ones = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
  if (number < 20) return ones[number];
  if (number < 100) return `${tens[Math.floor(number / 10)]}${number % 10 ? `-${ones[number % 10]}` : ''}`;
  if (number < 1000) return `${ones[Math.floor(number / 100)]} HUNDRED${number % 100 ? ` ${numberToWords(number % 100)}` : ''}`;
  if (number < 1000000) return `${numberToWords(Math.floor(number / 1000))} THOUSAND${number % 1000 ? ` ${numberToWords(number % 1000)}` : ''}`;
  return String(number);
};

export const downloadSenateResultsPackagePdf = async ({ institution, groups = [], generatedBy = '', scope = 'faculty', signatory = null, facultyName = '', senateApprover = null, senateBoardNumber: providedSenateBoardNumber = null, returnBlob = false }) => {
  const { jsPDF } = await import('jspdf');
  const departmentScope = scope === 'department';
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  // Senate prose follows the supplied document convention: Times New Roman
  // styling at 12 pt with approximately 1.5 line spacing. Tables retain
  // their compact, calculated row sizing so long programme names and reasons
  // do not overlap or distort the summary grid.
  const standardFontSize = 12;
  const standardLineHeight = 6.35;
  // Candidate registers use single spacing; narrative/header descriptions
  // continue to use standardLineHeight (approximately 1.5 spacing).
  const listLineHeight = 4.25;
  const listRowPadding = 1.25;
  let pageWidth = 210;
  let pageHeight = 297;
  const margin = 12;
  let contentWidth = pageWidth - (margin * 2);
  const footerHeight = 12;
  let y = margin;
  let pageNumber = 1;

  const loadLogo = async url => {
    if (!url) return null;
    try {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.src = url;
      await new Promise(resolve => { image.onload = resolve; image.onerror = resolve; });
      return image.width && image.height ? image : null;
    } catch (_) {
      return null;
    }
  };

  const drawFooter = () => {
    pdf.setDrawColor(180, 185, 195);
    pdf.setLineWidth(0.2);
    pdf.line(margin, pageHeight - footerHeight, pageWidth - margin, pageHeight - footerHeight);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(95, 105, 120);
    pdf.text('Senate examination results package · Marks retained in faculty records', margin, pageHeight - 5);
    pdf.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
    pdf.setTextColor(0, 0, 0);
  };

  const newPage = () => {
    drawFooter();
    pdf.addPage();
    pageNumber += 1;
    y = margin;
  };

  const addLandscapeContentPage = () => {
    drawFooter();
    pdf.addPage('a4', 'landscape');
    pageWidth = 297;
    pageHeight = 210;
    contentWidth = pageWidth - (margin * 2);
    pageNumber += 1;
    y = margin;
  };

  const ensureSpace = height => {
    if (y + height > pageHeight - footerHeight - 4) newPage();
  };

  // Senate list separators use one consistent rule regardless of whether a
  // page started fresh or was reached through a continuation after the
  // footer (which deliberately uses a lighter gray rule).
  const drawListRule = (x1, y1, x2, y2) => {
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.25);
    pdf.line(x1, y1, x2, y2);
  };

  const drawHeading = (title, subtitle = '') => {
    ensureSpace(18);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(12);
    pdf.text(title, margin, y);
    y += 5;
    if (subtitle) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFont('times', 'normal');
      pdf.setFontSize(standardFontSize);
      y = drawWrapped(pdf, subtitle, margin, y, contentWidth, standardLineHeight);
      y += 2;
    }
  };

  const drawTable = (headers, rows, widths, options = {}) => {
    const headerHeight = options.headerHeight || 8;
    const rowLineHeight = options.rowLineHeight || 3.6;
    const fontSize = options.fontSize || 7.5;
    const rowPadding = options.rowPadding || 2.5;
    const totalWidth = widths.reduce((sum, value) => sum + value, 0);
    const drawHeader = () => {
      pdf.setFillColor(31, 45, 65);
      pdf.setDrawColor(165, 175, 190);
      pdf.rect(margin, y, totalWidth, headerHeight, 'FD');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(fontSize);
      pdf.setTextColor(255, 255, 255);
      let x = margin;
      headers.forEach((header, index) => {
        pdf.text(text(header), x + (widths[index] / 2), y + 5.3, { align: 'center', maxWidth: widths[index] - 2 });
        x += widths[index];
      });
      pdf.setTextColor(0, 0, 0);
      y += headerHeight;
    };
    ensureSpace(headerHeight + 5);
    drawHeader();
    rows.forEach(row => {
      const lines = row.map((value, index) => pdf.splitTextToSize(text(value) || '—', widths[index] - 3));
      const lineCount = Math.max(...lines.map(value => value.length), 1);
      const rowHeight = Math.max(7, (lineCount * rowLineHeight) + rowPadding);
      if (y + rowHeight > pageHeight - footerHeight - 4) {
        newPage();
        drawHeader();
      }
      const fill = rows.indexOf(row) % 2 === 0 ? [255, 255, 255] : [246, 248, 251];
      pdf.setFillColor(...fill);
      pdf.setDrawColor(210, 215, 222);
      pdf.rect(margin, y, totalWidth, rowHeight, 'FD');
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(fontSize);
      let x = margin;
      lines.forEach((cellLines, index) => {
        pdf.text(cellLines, x + (index === 0 ? widths[index] / 2 : 2), y + 4.5, { align: index === 0 ? 'center' : 'left', maxWidth: widths[index] - 3 });
        x += widths[index];
      });
      let gridX = margin;
      widths.slice(0, -1).forEach(width => { gridX += width; pdf.line(gridX, y, gridX, y + rowHeight); });
      y += rowHeight;
    });
    y += 4;
  };

  const drawSummaryTable = rows => {
    const headers = [
      '', 'COMPETENT', 'NOT YET COMPETENT', 'RETAKE ONE', 'ACADEMIC LEAVE',
      'ABSCONDMENT', 'DEREGISTRATION', 'DEFER', 'WITHHOLD',
      'IN NOT YET COMPETENT AND RETAKE ONE', 'TOTAL PER PROGRAMME',
    ];
    const labelWidth = 76;
    const numberWidth = (contentWidth - labelWidth) / (headers.length - 1);
    const headerHeight = 58;
    const rowHeight = 7;
    const totalWidth = contentWidth;
    const drawGrid = height => {
      let x = margin + labelWidth;
      pdf.line(margin, y, margin + totalWidth, y);
      pdf.line(margin, y + height, margin + totalWidth, y + height);
      pdf.line(margin, y, margin, y + height);
      pdf.line(margin + labelWidth, y, margin + labelWidth, y + height);
      for (let index = 1; index < headers.length; index += 1) {
        x += numberWidth;
        pdf.line(x, y, x, y + height);
      }
    };
    const drawHeader = () => {
      ensureSpace(headerHeight + rowHeight);
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(20, 20, 20);
      pdf.rect(margin, y, totalWidth, headerHeight, 'FD');
      pdf.setFont('times', 'bold');
      pdf.setFontSize(7.2);
      headers.slice(1).forEach((header, index) => {
        const x = margin + labelWidth + (index * numberWidth) + (numberWidth / 2);
        // Rotated labels must remain a single line. Passing maxWidth here
        // makes jsPDF wrap the text before rotating it, which causes labels
        // to collide in the narrow summary columns.
        pdf.text(header, x, y + headerHeight - 3, { align: 'center', angle: 90 });
      });
      drawGrid(headerHeight);
      y += headerHeight;
    };
    const drawRow = row => {
      const height = row.kind === 'level' ? 7 : rowHeight;
      if (y + height > pageHeight - footerHeight - 4) {
        newPage();
        drawHeader();
      }
      pdf.setFillColor(255, 255, 255);
      pdf.setDrawColor(20, 20, 20);
      pdf.rect(margin, y, totalWidth, height, 'FD');
      if (row.kind === 'level' || row.kind === 'module') {
        pdf.setFont('times', 'bold');
        pdf.setFontSize(row.kind === 'level' ? 8.5 : 8);
        pdf.text(row.label, margin + 2, y + 4.8);
      } else {
        pdf.setFont('times', row.kind === 'total' || row.kind === 'grand' ? 'bold' : 'normal');
        pdf.setFontSize(7.5);
        pdf.text(row.label, margin + 2, y + 4.8, { maxWidth: labelWidth - 4 });
        row.values.forEach((value, index) => {
          const x = margin + labelWidth + (index * numberWidth) + (numberWidth / 2);
          pdf.text(String(value ?? 0), x, y + 4.8, { align: 'center' });
        });
      }
      drawGrid(height);
      y += height;
    };
    drawHeader();
    rows.forEach(drawRow);
    y += 5;
  };

  const levelNumberOf = value => {
    const match = String(value ?? '').match(/\d+/);
    return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
  };
  const sortedGroups = [...groups].sort((a, b) => {
    const periodOrder = text(a.period).localeCompare(text(b.period));
    if (periodOrder) return periodOrder;
    const levelOrder = levelNumberOf(a.programLevel) - levelNumberOf(b.programLevel);
    if (levelOrder) return levelOrder;
    return `${a.departmentName}${a.programName}${a.moduleNumber}`.localeCompare(`${b.departmentName}${b.programName}${b.moduleNumber}`);
  });
  const periodLabels = [...new Set(sortedGroups.map(group => group.period).filter(Boolean))];
  const facultyLabels = [...new Set([
    facultyName,
    signatory?.faculty,
    ...sortedGroups.map(group => group.facultyName),
  ].map(text).filter(value => value && !/not specified/i.test(value)))];
  const departmentLabels = [...new Set(sortedGroups.map(group => group.departmentName).filter(value => value && !/not specified/i.test(value)))];
  const departmentSections = new Map();
  sortedGroups.forEach(group => {
    const departmentName = group.departmentName || 'Department not specified';
    const facultyName = group.facultyName || 'Faculty / School not specified';
    const key = [group.period || 'Academic period not specified', facultyName, departmentName].join('|');
    const section = departmentSections.get(key) || {
      period: group.period || 'Academic period not specified',
      facultyName,
      departmentName,
      groups: [],
      programmes: new Set(),
      levels: new Set(),
      modules: new Set(),
      candidateEntries: 0,
    };
    section.groups.push(group);
    section.programmes.add(group.programName || 'Programme not specified');
    section.levels.add(group.programLevel == null ? '—' : `Level ${group.programLevel}`);
    section.modules.add(group.moduleNumber == null ? '—' : `Module ${group.moduleNumber}`);
    section.candidateEntries += (group.students || []).length;
    departmentSections.set(key, section);
  });
  const departmentSummaryRows = [...departmentSections.values()].map(section => [
    section.period,
    section.facultyName,
    section.departmentName,
    [...section.programmes].join(', '),
    [...section.levels].join(', '),
    [...section.modules].join(', '),
    section.groups.length,
    section.candidateEntries,
  ]);

  // Cover metadata is read from institution settings and the result hierarchy.
  // Optional senate fields are supported when configured; no faculty or board
  // name is hard-coded into the generated document.
  const institutionName = text(institution?.name) || 'Institution';
  const academicUnitLabel = text(
    institution?.academic_unit_name
      || institution?.institute_name
      || institution?.college_name
      || institution?.campus_name
      || (/\btvet\b/i.test(institutionName) ? institutionName.match(/\btvet(?:\s+institute)?/i)?.[0] : '')
  );
  const facultyCoverLabel = facultyLabels.length === 1 ? facultyLabels[0] : facultyLabels.length > 1 ? facultyLabels.join(' · ') : '';
  const departmentCoverLabel = departmentLabels.length === 1 ? departmentLabels[0] : departmentScope && departmentLabels.length > 1 ? `${departmentLabels.length} departments` : '';
  // Senate pages are faculty-facing. Use the faculty name from the approved
  // result hierarchy as the document header instead of the generic
  // "ACADEMIC INSTITUTE" placeholder. Institution settings remain a fallback
  // for legacy records that do not carry faculty metadata.
  const documentFacultyLabel = facultyCoverLabel || academicUnitLabel || 'FACULTY / SCHOOL';
  const senateInstituteLabel = academicUnitLabel || 'TVET Institute';
  const senateBoardNumber = providedSenateBoardNumber ?? institution?.senate_board_number ?? institution?.senate_session_number ?? institution?.senate_board_no;
  const senateBoardTitle = text(institution?.senate_board_title || institution?.senate_board_name) || 'Senate Board of Examiners';
  const senateBoardLine = senateBoardNumber == null || text(senateBoardNumber) === '' ? senateBoardTitle : `${ordinalLabel(senateBoardNumber)} ${senateBoardTitle}`;
  // The cover is a packet identity page, not a period listing. Keep the
  // academic period on the summary/results pages and show the calendar date
  // on the cover, matching the supplied Senate document.
  const now = new Date();
  const coverDateLabel = `${now.toLocaleString('en-GB', { month: 'long' }).toUpperCase()}, ${now.getFullYear()}`;
  const summaryPeriodLabel = periodLabels.map(period => {
    const [year, term] = String(period).split(' · ');
    return year && term ? `${term}, ${year} ACADEMIC YEAR` : period;
  }).join(' · ') || coverDateLabel;
  const deanName = text(signatory?.name || institution?.dean_name || institution?.faculty_dean_name || institution?.dean?.full_name) || 'NAME OF DEAN';
  const deanRole = text(signatory?.role || institution?.dean_title) || 'DEAN OF FACULTY';
  const senateApproverName = text(
    senateApprover?.name
      || institution?.vice_chancellor_name
      || institution?.vice_chancellor?.full_name
      || institution?.senate_approver_name
      || institution?.senate_approver?.full_name,
  );
  const senateApproverRoleBase = text(senateApprover?.role || institution?.vice_chancellor_title || institution?.senate_approver_title);
  const senateApproverRole = senateApproverRoleBase
    ? (senateApproverRoleBase.toUpperCase().includes(institutionName.toUpperCase())
      ? senateApproverRoleBase
      : `${senateApproverRoleBase}, ${institutionName}`)
    : `VICE-CHANCELLOR, ${institutionName}`;
  const drawSignatureBlock = (x, name, role) => {
    // Keep the two signature fields proportional instead of stretching the
    // signature line across the remaining page width. The date is centered
    // over its own line, matching the supplied Senate document layout.
    const signatureLineWidth = Math.min(85, Math.max(58, pageWidth - x - margin - 6 - 16 - 48));
    const signatureDateGap = 16;
    const dateLineWidth = 48;
    const blockWidth = signatureLineWidth + signatureDateGap + dateLineWidth;
    const dateLineStart = x + blockWidth - dateLineWidth;
    const signatureLineEnd = x + signatureLineWidth;
    const dateLineEnd = dateLineStart + dateLineWidth;
    pdf.setDrawColor(20, 20, 20);
    pdf.setLineWidth(0.25);
    pdf.line(x, y, signatureLineEnd, y);
    pdf.line(dateLineStart, y, dateLineEnd, y);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(standardFontSize);
    pdf.text(name.toUpperCase(), x, y + 6);
    pdf.setFontSize(standardFontSize);
    pdf.text(role.toUpperCase(), x, y + 11, { maxWidth: signatureLineWidth });
    pdf.setFont('times', 'bold');
    pdf.setFontSize(standardFontSize);
    pdf.text('DATE', dateLineStart + (dateLineWidth / 2), y + 6, { align: 'center' });
  };

  const coverCenter = pageWidth / 2;
  const coverLogo = await loadLogo(institution?.logo_url || '/logo192.png');
  if (coverLogo) {
    const logoHeight = 25;
    const logoWidth = (coverLogo.width / coverLogo.height) * logoHeight;
    pdf.addImage(coverLogo, 'PNG', coverCenter - (logoWidth / 2), 10, logoWidth, logoHeight);
  }
  pdf.setFont('times', 'bold');
  pdf.setFontSize(20);
  pdf.text(institutionName.toUpperCase(), coverCenter, 47, { align: 'center' });
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.line(margin, 52, pageWidth - margin, 52);
  // The cover identifies the faculty/school being presented. Keep the
  // faculty name prominent and dynamically sourced from the approved result
  // hierarchy (with the academic institute as a legacy fallback).
  const coverLines = [documentFacultyLabel].filter(Boolean);
  if (coverLines.length) {
    pdf.setFontSize(36);
    pdf.text(coverLines[0].toUpperCase(), coverCenter, 105, { align: 'center', maxWidth: pageWidth - 40 });
    coverLines.slice(1).forEach((line, index) => {
      pdf.setFontSize(index === coverLines.length - 2 ? 20 : 24);
      pdf.text(line.toUpperCase(), coverCenter, 124 + (index * 17), { align: 'center', maxWidth: pageWidth - 30 });
    });
  }
  pdf.setFontSize(20);
  pdf.text(senateBoardLine.toUpperCase(), coverCenter, 205, { align: 'center', maxWidth: pageWidth - 30 });
  pdf.setFont('times', 'normal');
  pdf.setFontSize(standardFontSize);
  // The calendar month/year sits immediately above the closing rule.
  pdf.text(coverDateLabel, coverCenter, pageHeight - 23, { align: 'center', maxWidth: pageWidth - 30 });
  pdf.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);

  // Page 2 follows the supplied Senate document: institution header followed
  // by a compact, grouped results-summary table.
  pdf.addPage('a4', 'portrait');
  pageWidth = 210;
  pageHeight = 297;
  contentWidth = pageWidth - (margin * 2);
  pageNumber += 1;
  y = margin;
  if (coverLogo) {
    const logoHeight = 25;
    const logoWidth = (coverLogo.width / coverLogo.height) * logoHeight;
    pdf.addImage(coverLogo, 'PNG', (pageWidth / 2) - (logoWidth / 2), 8, logoWidth, logoHeight);
  }
  pdf.setFont('times', 'bold');
  pdf.setFontSize(16);
  pdf.text(institutionName.toUpperCase(), pageWidth / 2, 46, { align: 'center' });
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.3);
  pdf.line(margin, 50, pageWidth - margin, 50);
  pdf.setFontSize(13);
  pdf.text(documentFacultyLabel.toUpperCase(), pageWidth / 2, 57, { align: 'center', maxWidth: contentWidth });
  pdf.setFontSize(11);
  pdf.text(summaryPeriodLabel.toUpperCase(), pageWidth / 2, 66, { align: 'center', maxWidth: contentWidth });
  pdf.setFontSize(12);
  pdf.text('RESULTS SUMMARY TABLE', pageWidth / 2, 74, { align: 'center' });
  y = 79;

  const summaryTableRows = [];
  const levelGroups = new Map();
  sortedGroups.forEach(group => {
    const levelKey = group.programLevel == null ? '—' : String(group.programLevel);
    const moduleKey = group.moduleNumber == null ? '—' : String(group.moduleNumber);
    const level = levelGroups.get(levelKey) || new Map();
    const module = level.get(moduleKey) || [];
    module.push(group);
    level.set(moduleKey, module);
    levelGroups.set(levelKey, level);
  });
  const levelWord = value => ({ 3: 'THREE', 4: 'FOUR', 5: 'FIVE', 6: 'SIX' }[Number(value)] || value);
  const moduleWord = value => ({ 1: 'ONE', 2: 'TWO', 3: 'THREE', 4: 'FOUR', 5: 'FIVE' }[Number(value)] || value);
  const valuesForGroup = group => {
    const counts = senateGroupSummary(group);
    return [
      counts.competent,
      counts.not_yet_competent,
      counts.retake_one,
      counts.academic_leave,
      counts.absconded,
      counts.deregistration,
      counts.defer,
      counts.withhold,
      counts.not_yet_competent + counts.retake_one,
      (group.students || []).length,
    ];
  };
  const addTotals = (target, values) => values.forEach((value, index) => { target[index] += Number(value) || 0; });
  [...levelGroups.entries()]
    .sort(([a], [b]) => (Number(a) || 999) - (Number(b) || 999))
    .forEach(([levelKey, modules]) => {
      summaryTableRows.push({ kind: 'level', label: `LEVEL ${levelWord(levelKey)}`.toUpperCase() });
      [...modules.entries()]
        .sort(([a], [b]) => (Number(a) || 999) - (Number(b) || 999))
        .forEach(([moduleKey, moduleGroups]) => {
          summaryTableRows.push({ kind: 'module', label: `MODULE ${moduleWord(moduleKey)}`.toUpperCase() });
          const moduleTotals = Array(10).fill(0);
          moduleGroups.forEach(group => {
            const values = valuesForGroup(group);
            addTotals(moduleTotals, values);
            summaryTableRows.push({ kind: 'programme', label: (group.programName || 'PROGRAMME').toUpperCase(), values });
          });
          summaryTableRows.push({ kind: 'total', label: 'TOTAL PER CATEGORY', values: moduleTotals });
        });
    });
  const grandTotals = Array(10).fill(0);
  summaryTableRows.forEach(row => { if (row.values) addTotals(grandTotals, row.values); });
  summaryTableRows.push({ kind: 'grand', label: 'GRAND TOTAL', values: grandTotals });
  drawSummaryTable(summaryTableRows);
  ensureSpace(28);
  drawSignatureBlock(margin + 6, deanName, deanRole);
  y += 23;

  // The supplied Senate document starts each results section with a dedicated
  // portrait title page. Keep that page separate from the landscape candidate
  // tables that follow it so the section heading remains legible when printed.
  const formatAcademicYear = value => {
    const raw = text(value);
    const match = raw.match(/^(\d{4})\s*[-/]\s*(\d{2}|\d{4})$/);
    if (!match) return raw;
    const end = match[2].length === 2 ? `${match[1].slice(0, 2)}${match[2]}` : match[2];
    return `${match[1]}/${end}`;
  };
  const formatSectionPeriod = value => {
    const [academicYear, term] = String(value || '').split(' · ');
    const normalizedTerm = text(term || value).toUpperCase();
    const termLabel = /JAN\s*[-/]?\s*APR|SECOND\s+SEMESTER|SEMESTER\s*2/.test(normalizedTerm)
      ? 'SECOND SEMESTER'
      : /SEP\s*[-/]?\s*DEC|FIRST\s+SEMESTER|SEMESTER\s*1/.test(normalizedTerm)
        ? 'FIRST SEMESTER'
        : /MAY\s*[-/]?\s*AUG|TRIMESTER/.test(normalizedTerm)
          ? 'TRIMESTER'
          : normalizedTerm || 'EXAMINATION RESULTS';
    const yearLabel = formatAcademicYear(academicYear || '');
    return yearLabel ? `${termLabel}, ${yearLabel}` : termLabel;
  };
  const addPortraitSectionTitlePage = (group, levelValue = group?.programLevel) => {
    // Close the preceding page before changing orientation so every page in
    // the packet receives the same footer and page number treatment.
    drawFooter();
    pdf.addPage('a4', 'portrait');
    pageWidth = 210;
    pageHeight = 297;
    contentWidth = pageWidth - (margin * 2);
    pageNumber += 1;
    y = margin;
    const center = pageWidth / 2;
    if (coverLogo) {
      const logoHeight = 25;
      const logoWidth = (coverLogo.width / coverLogo.height) * logoHeight;
      pdf.addImage(coverLogo, 'PNG', center - (logoWidth / 2), 8, logoWidth, logoHeight);
    }
    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    pdf.text(institutionName.toUpperCase(), center, 46, { align: 'center' });
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(margin, 50, pageWidth - margin, 50);
    pdf.setFontSize(13);
    pdf.text(documentFacultyLabel.toUpperCase(), center, 57, { align: 'center', maxWidth: contentWidth });

    const periodTitle = formatSectionPeriod(group?.period || periodLabels[0]);
    const [termTitle, yearTitle] = periodTitle.split(', ');
    pdf.setFontSize(18);
    if (yearTitle) {
      pdf.text(`${termTitle}, ${yearTitle}`, center, 143, { align: 'center', maxWidth: contentWidth });
      pdf.setFontSize(18);
      pdf.text('ACADEMIC YEAR', center, 154, { align: 'center' });
    } else {
      pdf.text(termTitle, center, 143, { align: 'center', maxWidth: contentWidth });
    }
    const levelNumber = levelNumberOf(levelValue);
    const levelTitleWord = ({ 3: 'THREE', 4: 'FOUR', 5: 'FIVE', 6: 'SIX' }[levelNumber] || text(levelValue) || '—').toUpperCase();
    pdf.setFontSize(18);
    pdf.text(`EXAMINATIONS RESULTS - LEVEL ${levelTitleWord}`, center, 190, { align: 'center', maxWidth: contentWidth });
  };

  const levelYearWord = levelSection => {
    // Programme level (for example Level 6) is not the academic year. The
    // curriculum advances two modules per year: Modules 1–2 are Year 1,
    // Modules 3–4 are Year 2, and so on. Use the modules represented in the
    // level section rather than assuming Level 6 means Year 4.
    const moduleNumbers = (levelSection?.groups || [])
      .map(group => Number(group.moduleNumber))
      .filter(module => Number.isFinite(module) && module > 0);
    const academicYear = moduleNumbers.length ? Math.ceil(Math.min(...moduleNumbers) / 2) : null;
    const fallbackYear = ({ 3: 'ONE', 4: 'TWO', 5: 'THREE', 6: 'FOUR' }[levelNumberOf(levelSection?.level)]);
    return ({ 1: 'ONE', 2: 'TWO', 3: 'THREE', 4: 'FOUR', 5: 'FIVE', 6: 'SIX' }[academicYear] || fallbackYear || '—');
  };
  const periodParts = value => {
    const periodTitle = formatSectionPeriod(value);
    const [termTitle, yearTitle] = periodTitle.split(', ');
    const termWord = termTitle.includes('SECOND') ? 'SECOND' : termTitle.includes('FIRST') ? 'FIRST' : termTitle.includes('TRIMESTER') ? 'TRIMESTER' : termTitle;
    return { termTitle, termWord, yearTitle };
  };
  const formatApprovalDate = levelSection => {
    const timestamps = levelSection.groups
      .flatMap(group => (group.batches || []).flatMap(batch => [batch.dean_presented_at, batch.hod_decision_at, batch.updated_at]))
      .filter(Boolean)
      .map(value => new Date(value))
      .filter(date => !Number.isNaN(date.getTime()))
      .sort((a, b) => b.getTime() - a.getTime());
    const date = timestamps[0] || new Date();
    return `${ordinalLabel(date.getDate())} ${date.toLocaleString('en-GB', { month: 'long' }).toUpperCase()}, ${date.getFullYear()}`;
  };
  const levelCompetentProgrammes = levelSection => {
    const programmes = new Map();
    levelSection.groups.forEach(group => {
      const programmeKey = String(group.programId || group.programCode || group.programName || 'programme');
      const programme = programmes.get(programmeKey) || {
        name: group.programName || group.programCode || 'Programme',
        students: new Map(),
      };
      senateCandidateRows(group).forEach(row => {
        // Competent includes every passing competency rating (Competent,
        // Proficient and Mastery); classifySenateStudent normalises them to
        // the single competent outcome used by the Senate summary.
        if (row.senateOutcome.key !== 'competent') return;
        const studentKey = String(row.admissionNumber || row.name || row.key || 'student');
        if (!programme.students.has(studentKey)) {
          programme.students.set(studentKey, {
            admissionNumber: row.admissionNumber || '—',
            name: row.name || 'Unknown student',
          });
        }
      });
      programmes.set(programmeKey, programme);
    });
    return [...programmes.values()]
      .map(programme => ({ ...programme, students: [...programme.students.values()].sort((a, b) => String(a.admissionNumber).localeCompare(String(b.admissionNumber))) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const awardRank = mark => {
    const recorded = [
      mark?.rating,
      mark?.outcome,
      mark?.competency,
      mark?.grade_letter,
    ].map(text).join(' ').toLowerCase();
    const result = getSummativeCompetency(mark);
    const competency = `${recorded} ${String(result.remark || '').toLowerCase()}`;
    if (/mastery|attained\s+mastery/.test(competency)) return 3;
    if (/proficient/.test(competency)) return 2;
    if (/competent/.test(competency)) return 1;
    return 0;
  };

  const awardCategoryLabel = rank => (rank >= 3 ? 'ATTAINED MASTERY' : rank === 2 ? 'PROFICIENT' : 'COMPETENT');

  // A candidate reaches the award list only when every unit represented for
  // that programme/level in the Senate packet has a passing result. The
  // overall category is the lowest proficiency attained across those units.
  const levelAwardProgrammes = levelSection => {
    const programmeMap = new Map();
    levelSection.groups.forEach(group => {
      const programmeKey = String(group.programId || group.programCode || group.programName || 'programme');
      const programme = programmeMap.get(programmeKey) || {
        name: group.programName || group.programCode || 'Programme',
        groups: [],
        students: new Map(),
      };
      programme.groups.push(group);
      (group.students || []).forEach(student => {
        const studentKey = String(student.admissionNumber || student.studentId || student.name || student.key || 'student');
        const candidate = programme.students.get(studentKey) || {
          admissionNumber: student.admissionNumber,
          name: student.name,
          status: student.status,
          enrollmentStatus: student.enrollmentStatus || student.enrollment_status,
          marks: new Map(),
          groups: new Set(),
        };
        if (student.status) candidate.status = student.status;
        if (student.enrollmentStatus || student.enrollment_status) candidate.enrollmentStatus = student.enrollmentStatus || student.enrollment_status;
        candidate.groups.add(group.key);
        student.marks?.forEach((mark, unitKey) => candidate.marks.set(`${group.key}|${unitKey}`, mark));
        programme.students.set(studentKey, candidate);
      });
      programmeMap.set(programmeKey, programme);
    });

    return [...programmeMap.values()]
      .map(programme => {
        const requiredUnits = programme.groups.flatMap(group => (group.units || []).map(unit => ({
          key: `${group.key}|${unit.key}`,
          code: unit.code || unit.key,
        })));
        const categories = new Map([
          ['ATTAINED MASTERY', []],
          ['PROFICIENT', []],
          ['COMPETENT', []],
        ]);
        [...programme.students.values()].forEach(candidate => {
          if (!requiredUnits.length || requiredUnits.some(unit => !candidate.marks.has(unit.key))) return;
          const requiredMarks = requiredUnits.map(unit => candidate.marks.get(unit.key));
          if (requiredMarks.some(mark => !mark || String(getSummativeCompetency(mark).remark || '').toUpperCase().startsWith('NYC'))) return;
          const classification = classifySenateStudent({ ...candidate, marks: new Map(requiredUnits.map(unit => [unit.key, candidate.marks.get(unit.key)])) });
          if (classification.key !== 'competent') return;
          const overallRank = Math.min(...requiredMarks.map(awardRank));
          if (!overallRank) return;
          const category = awardCategoryLabel(overallRank);
          categories.get(category).push({
            admissionNumber: candidate.admissionNumber || '—',
            name: candidate.name || 'Unknown student',
            category,
          });
        });
        categories.forEach(students => students.sort((a, b) => String(a.admissionNumber).localeCompare(String(b.admissionNumber))));
        return { name: programme.name, categories };
      })
      .filter(programme => [...programme.categories.values()].some(students => students.length))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const buildDemoAwardSections = () => {
    const sections = new Map();
    DEMO_AWARD_CANDIDATES.forEach(candidate => {
      const levelKey = String(candidate.level);
      const section = sections.get(levelKey) || {
        level: candidate.level,
        groups: [{ key: `demo-level-${candidate.level}`, period: periodLabels[0] || 'Academic period', moduleNumber: candidate.level }],
        demo: true,
        programmes: new Map(),
      };
      const programme = section.programmes.get(candidate.programme) || {
        name: candidate.programme,
        categories: new Map([
          ['ATTAINED MASTERY', []],
          ['PROFICIENT', []],
          ['COMPETENT', []],
        ]),
      };
      programme.categories.get(candidate.category).push({
        admissionNumber: candidate.registrationNumber,
        name: candidate.name,
        category: candidate.category,
      });
      section.programmes.set(candidate.programme, programme);
      sections.set(levelKey, section);
    });
    return [...sections.values()]
      .map(section => ({ ...section, programmes: [...section.programmes.values()].sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => levelNumberOf(a.level) - levelNumberOf(b.level));
  };

  const addPortraitLevelSummaryPage = levelSection => {
    drawFooter();
    pdf.addPage('a4', 'portrait');
    pageWidth = 210;
    pageHeight = 297;
    contentWidth = pageWidth - (margin * 2);
    pageNumber += 1;
    y = margin;
    const center = pageWidth / 2;
    if (coverLogo) {
      const logoHeight = 25;
      const logoWidth = (coverLogo.width / coverLogo.height) * logoHeight;
      pdf.addImage(coverLogo, 'PNG', center - (logoWidth / 2), 8, logoWidth, logoHeight);
    }
    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    pdf.text(institutionName.toUpperCase(), center, 46, { align: 'center' });
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(margin, 50, pageWidth - margin, 50);
    pdf.setFontSize(13);
    pdf.text(documentFacultyLabel.toUpperCase(), center, 57, { align: 'center', maxWidth: contentWidth });

    const period = periodParts(levelSection.groups[0]?.period || periodLabels[0]);
    pdf.setFontSize(11.5);
    pdf.text(`${period.termTitle}, ${period.yearTitle || ''} ACADEMIC YEAR`.trim(), center, 67, { align: 'center', maxWidth: contentWidth });
    const levelWord = ({ 3: 'THREE', 4: 'FOUR', 5: 'FIVE', 6: 'SIX' }[levelNumberOf(levelSection.level)] || text(levelSection.level) || '—').toUpperCase();
    pdf.setFontSize(12);
    const termHeading = period.termWord === 'TRIMESTER' ? 'TRIMESTER' : `${period.termWord} SEMESTER`;
    pdf.text(`YEAR ${levelYearWord(levelSection)}, ${termHeading} LEVEL ${levelWord} EXAMINATION RESULTS`, center, 78, { align: 'center', maxWidth: contentWidth });
    pdf.setFontSize(12);
    pdf.text('COMPETENT', center, 88, { align: 'center' });
    pdf.setLineWidth(0.25);
    pdf.line(center - 19, 90, center + 19, 90);

    const programmes = levelCompetentProgrammes(levelSection);
    const competentCount = programmes.reduce((total, programme) => total + programme.students.length, 0);
    pdf.setFont('times', 'normal');
    pdf.setFontSize(standardFontSize);
    const paragraphSegments = [
      { text: 'The following ' },
      { text: `${numberToWords(competentCount)} (${competentCount})`, bold: true },
      { text: ` candidate${competentCount === 1 ? '' : 's'} satisfied the Board of Examiners of the ` },
      { text: senateInstituteLabel.toUpperCase(), bold: true },
      { text: ' in the ' },
      { text: `YEAR ${levelYearWord(levelSection)}`, bold: true },
      { text: ', ' },
      { text: termHeading.toUpperCase(), bold: true },
      { text: ' Examinations in the programmes indicated.' },
    ];
    y = drawRichWrapped(pdf, paragraphSegments, margin, 98, contentWidth, standardLineHeight);
    y += 5;

    let serial = 1;
    programmes.forEach(programme => {
      ensureSpace(16);
      pdf.setFont('times', 'bold');
      pdf.setFontSize(10);
      pdf.text(programme.name.toUpperCase(), margin, y);
      drawListRule(margin, y + 1.5, pageWidth - margin, y + 1.5);
      y += 6;
      pdf.setFontSize(9.5);
      pdf.text('S/NO.', margin + 2, y);
      pdf.text('REG. NO.', margin + 20, y);
      pdf.text('NAME', margin + 67, y);
      drawListRule(margin, y + 2, pageWidth - margin, y + 2);
      y += 5;
      programme.students.forEach(student => {
        if (y + listLineHeight > pageHeight - footerHeight - 4) newPage();
        pdf.setFont('times', 'normal');
        pdf.setFontSize(9.5);
        pdf.text(`${serial}.`, margin + 7, y, { align: 'right' });
        pdf.text(text(student.admissionNumber) || '—', margin + 20, y);
        pdf.text(text(student.name) || 'Unknown student', margin + 67, y, { maxWidth: contentWidth - 67 });
        serial += 1;
        y += listLineHeight;
      });
      // Use one rule to close the programme group. Individual candidate
      // rows remain open so the list reads as one continuous register.
      drawListRule(margin, y - 3, pageWidth - margin, y - 3);
      y += 2;
    });

    const boardApprovalText = `APPROVED BY THE BOARD OF EXAMINERS MEETING, ${documentFacultyLabel} HELD ON ${formatApprovalDate(levelSection)}.`.toUpperCase();
    const boardApprovalLineCount = Math.max(1, pdf.splitTextToSize(boardApprovalText, contentWidth).length);
    const boardApprovalHeight = boardApprovalLineCount * standardLineHeight;
    y += 6;
    ensureSpace(boardApprovalHeight + 7 + 18 + 18 + 25 + 18);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(standardFontSize);
    y = drawWrapped(pdf, boardApprovalText, margin, y, contentWidth, standardLineHeight);
    y += 7;
    ensureSpace(18);
    drawSignatureBlock(margin + 6, deanName, deanRole);
    y += 18;

    // Senate approval is a separate decision from the Board/dean forwarding
    // step. Keep the heading and signature area on the same level summary page
    // when space allows, matching the supplied Senate document structure.
    ensureSpace(25);
    pdf.setFont('times', 'normal');
    pdf.setFontSize(standardFontSize);
    pdf.text('APPROVED BY THE SENATE', margin, y + 2);
    y += 13;
    if (senateApproverName) {
      drawSignatureBlock(margin + 6, senateApproverName, senateApproverRole);
    } else {
      drawSignatureBlock(margin + 6, 'SENATE APPROVER', '');
    }
    y += 18;
  };

  const addPortraitAwardPage = levelSection => {
    const programmes = levelSection.programmes || levelAwardProgrammes(levelSection);
    if (!programmes.length) return;
    drawFooter();
    pdf.addPage('a4', 'portrait');
    pageWidth = 210;
    pageHeight = 297;
    contentWidth = pageWidth - (margin * 2);
    pageNumber += 1;
    y = margin;
    const center = pageWidth / 2;
    const period = periodParts(levelSection.groups?.[0]?.period || periodLabels[0]);
    const levelWord = ({ 3: 'THREE', 4: 'FOUR', 5: 'FIVE', 6: 'SIX' }[levelNumberOf(levelSection.level)] || text(levelSection.level) || '—').toUpperCase();
    const termHeading = period.termWord === 'TRIMESTER' ? 'TRIMESTER' : `${period.termWord} SEMESTER`;
    const yearWord = levelYearWord(levelSection);
    const candidateCount = programmes.reduce((total, programme) => total + [...programme.categories.values()].reduce((sum, rows) => sum + rows.length, 0), 0);

    const drawAwardRunningHeader = continuation => {
      if (!continuation) {
        if (coverLogo) {
          const logoHeight = 25;
          const logoWidth = (coverLogo.width / coverLogo.height) * logoHeight;
          pdf.addImage(coverLogo, 'PNG', center - (logoWidth / 2), 8, logoWidth, logoHeight);
        }
        pdf.setFont('times', 'bold');
        pdf.setFontSize(16);
        pdf.text(institutionName.toUpperCase(), center, 46, { align: 'center' });
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.line(margin, 50, pageWidth - margin, 50);
        pdf.setFontSize(13);
        pdf.text(documentFacultyLabel.toUpperCase(), center, 57, { align: 'center', maxWidth: contentWidth });
        pdf.setFontSize(11.5);
        pdf.text(`${period.termTitle}, ${period.yearTitle || ''} ACADEMIC YEAR`.trim(), center, 67, { align: 'center', maxWidth: contentWidth });
        pdf.setFontSize(12);
        pdf.text(`YEAR ${yearWord}, ${termHeading} LEVEL ${levelWord} EXAMINATION RESULTS`, center, 78, { align: 'center', maxWidth: contentWidth });
        pdf.setFontSize(14);
        const awardListTitle = `AWARD LIST${levelSection.demo ? ' — DEMO VERIFICATION DATA' : ''}`;
        pdf.text(awardListTitle, center, 91, { align: 'center', maxWidth: contentWidth });
        pdf.setLineWidth(0.25);
        // Match the underline to the actual heading width so longer dynamic
        // titles are not left with a short, unrelated rule.
        const underlineWidth = Math.min(pdf.getTextWidth(awardListTitle), contentWidth);
        pdf.line(center - (underlineWidth / 2), 93, center + (underlineWidth / 2), 93);
        y = 102;
      } else {
        // Continuation pages should not repeat the award-list title. Leave a
        // modest top margin and let the column header identify the continued
        // table instead.
        y = margin + 8;
      }
    };
    drawAwardRunningHeader(false);

    pdf.setFont('times', 'normal');
    pdf.setFontSize(standardFontSize);
    const narrative = [
      { text: 'The following ' },
      { text: `${numberToWords(candidateCount)} (${candidateCount})`, bold: true },
      { text: ` candidate${candidateCount === 1 ? '' : 's'} satisfied the Board of Examiners of the ` },
      { text: senateInstituteLabel.toUpperCase(), bold: true },
      { text: ' in the ' },
      { text: `YEAR ${yearWord}, ${termHeading}`, bold: true, noSplit: true },
      { text: ' Examinations in the programme' + (candidateCount === 1 ? '' : 's') + ' indicated. The Institute Board of Examiners therefore recommends that ' + (candidateCount === 1 ? 'the candidate be ' : 'the candidates be ') },
      { text: `AWARDED LEVEL ${levelWord} CERTIFICATE`, bold: true, noSplit: true },
      { text: ` in the categor${candidateCount === 1 ? 'y' : 'ies'} indicated.` },
    ];
    y = drawRichWrapped(pdf, narrative, margin, y, contentWidth, standardLineHeight);
    y += 5;

    let serial = 1;
    const categoryOrder = ['ATTAINED MASTERY', 'PROFICIENT', 'COMPETENT'];
    const drawAwardTableHeader = () => {
      pdf.setFont('times', 'bold');
      pdf.setFontSize(standardFontSize);
      pdf.text('S/NO.', margin + 2, y);
      pdf.text('REG. NO.', margin + 25, y);
      pdf.text('NAME', margin + 75, y);
      drawListRule(margin, y + 2, pageWidth - margin, y + 2);
      y += 7;
    };

    programmes.forEach(programme => {
      categoryOrder.forEach(category => {
        const students = programme.categories.get(category) || [];
        if (!students.length) return;
        ensureSpace(25);
        pdf.setFont('times', 'bold');
        pdf.setFontSize(standardFontSize);
        pdf.text(programme.name.toUpperCase(), margin, y);
        y += 6;
        pdf.text(category, margin, y);
        drawListRule(margin, y + 1.5, pageWidth - margin, y + 1.5);
        y += 6;
        drawAwardTableHeader();
        students.forEach(student => {
          const nameLines = pdf.splitTextToSize(text(student.name) || 'Unknown student', contentWidth - 75);
          const rowHeight = Math.max(5.5, (nameLines.length * listLineHeight) + listRowPadding);
          if (y + rowHeight > pageHeight - footerHeight - 35) {
            newPage();
            drawAwardRunningHeader(true);
            drawAwardTableHeader();
          }
          pdf.setFont('times', 'normal');
          pdf.setFontSize(standardFontSize);
          pdf.text(`${serial}.`, margin + 9, y + listLineHeight, { align: 'right' });
          pdf.text(text(student.admissionNumber) || '—', margin + 25, y + listLineHeight);
          pdf.text(nameLines, margin + 75, y + listLineHeight);
          serial += 1;
          // Leave a small gap before the next candidate or group heading so
          // the 12 pt text stays comfortably separated from the group rule.
          y += rowHeight + listRowPadding;
        });
        // Close each programme/proficiency group once, rather than drawing a
        // horizontal rule beneath every candidate row.
        drawListRule(margin, y - listRowPadding, pageWidth - margin, y - listRowPadding);
        y += 3;
      });
    });

    const boardApprovalText = `APPROVED BY THE BOARD OF EXAMINERS MEETING, ${documentFacultyLabel} HELD ON ${formatApprovalDate(levelSection)}.`.toUpperCase();
    const boardApprovalLineCount = Math.max(1, pdf.splitTextToSize(boardApprovalText, contentWidth).length);
    y += 6;
    ensureSpace((boardApprovalLineCount * standardLineHeight) + 7 + 18 + 25 + 18);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(standardFontSize);
    y = drawWrapped(pdf, boardApprovalText, margin, y, contentWidth, standardLineHeight);
    y += 7;
    drawSignatureBlock(margin + 6, deanName, deanRole);
    y += 18;
    ensureSpace(25);
    pdf.setFont('times', 'normal');
    pdf.setFontSize(standardFontSize);
    pdf.text('APPROVED BY THE SENATE', margin, y + 2);
    y += 13;
    drawSignatureBlock(margin + 6, senateApproverName || 'SENATE APPROVER', senateApproverRole);
  };

  const addPortraitAwardTitlePage = levelSection => {
    drawFooter();
    pdf.addPage('a4', 'portrait');
    pageWidth = 210;
    pageHeight = 297;
    contentWidth = pageWidth - (margin * 2);
    pageNumber += 1;
    y = margin;
    const center = pageWidth / 2;
    if (coverLogo) {
      const logoHeight = 25;
      const logoWidth = (coverLogo.width / coverLogo.height) * logoHeight;
      pdf.addImage(coverLogo, 'PNG', center - (logoWidth / 2), 8, logoWidth, logoHeight);
    }
    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    pdf.text(institutionName.toUpperCase(), center, 46, { align: 'center' });
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(margin, 50, pageWidth - margin, 50);
    pdf.setFontSize(13);
    pdf.text(documentFacultyLabel.toUpperCase(), center, 57, { align: 'center', maxWidth: contentWidth });
    const levelWord = ({ 3: 'THREE', 4: 'FOUR', 5: 'FIVE', 6: 'SIX' }[levelNumberOf(levelSection.level)] || text(levelSection.level) || '—').toUpperCase();
    pdf.setFontSize(18);
    pdf.text(`LEVEL ${levelWord} AWARD LIST${levelSection.demo ? ' — DEMO VERIFICATION DATA' : ''}`, center, 190, { align: 'center', maxWidth: contentWidth });
  };

  // Build one overall candidate outcome per programme/level, combining all
  // modules represented in the section. This prevents a candidate with two
  // NYC units in different modules from being shown as two separate
  // RETAKE ONE entries.
  const outcomeCandidatesForLevel = (levelSection, outcomeKey) => {
    const programmeMap = new Map();
    levelSection.groups.forEach(group => {
      const programmeKey = String(group.programId || group.programCode || group.programName || 'programme');
      const programme = programmeMap.get(programmeKey) || {
        name: group.programName || group.programCode || 'Programme',
        students: new Map(),
      };
      (group.students || []).forEach(student => {
        const studentKey = String(student.admissionNumber || student.studentId || student.name || student.key || 'student');
        const candidate = programme.students.get(studentKey) || {
          key: studentKey,
          studentId: student.studentId,
          admissionNumber: student.admissionNumber,
          name: student.name,
          status: student.status,
          enrollmentStatus: student.enrollmentStatus || student.enrollment_status,
          abscondmentCount: Number(student.abscondmentCount || 0),
          marks: new Map(),
        };
        if (student.status) candidate.status = student.status;
        if (student.enrollmentStatus || student.enrollment_status) candidate.enrollmentStatus = student.enrollmentStatus || student.enrollment_status;
        candidate.abscondmentCount = Math.max(candidate.abscondmentCount, Number(student.abscondmentCount || 0));
        student.marks?.forEach((mark, unitKey) => candidate.marks.set(`${group.key}|${unitKey}`, mark));
        programme.students.set(studentKey, candidate);
      });
      programmeMap.set(programmeKey, programme);
    });
    return [...programmeMap.values()]
      .map(programme => ({
        ...programme,
        students: [...programme.students.values()]
          .map(student => ({ ...student, senateOutcome: classifySenateStudent(student) }))
          .filter(student => student.senateOutcome.key === outcomeKey)
          .sort((a, b) => String(a.admissionNumber || a.name).localeCompare(String(b.admissionNumber || b.name))),
      }))
      .filter(programme => programme.students.length);
  };

  const addPortraitOutcomePage = (levelSection, outcomeKey) => {
    const programmes = outcomeCandidatesForLevel(levelSection, outcomeKey);
    if (!programmes.length) return;
    drawFooter();
    pdf.addPage('a4', 'portrait');
    pageWidth = 210;
    pageHeight = 297;
    contentWidth = pageWidth - (margin * 2);
    pageNumber += 1;
    y = margin;
    const center = pageWidth / 2;
    if (coverLogo) {
      const logoHeight = 25;
      const logoWidth = (coverLogo.width / coverLogo.height) * logoHeight;
      pdf.addImage(coverLogo, 'PNG', center - (logoWidth / 2), 8, logoWidth, logoHeight);
    }
    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    pdf.text(institutionName.toUpperCase(), center, 46, { align: 'center' });
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    pdf.line(margin, 50, pageWidth - margin, 50);
    pdf.setFontSize(13);
    pdf.text(documentFacultyLabel.toUpperCase(), center, 57, { align: 'center', maxWidth: contentWidth });

    const period = periodParts(levelSection.groups[0]?.period || periodLabels[0]);
    pdf.setFontSize(11.5);
    pdf.text(`${period.termTitle}, ${period.yearTitle || ''} ACADEMIC YEAR`.trim(), center, 67, { align: 'center', maxWidth: contentWidth });
    const levelWord = ({ 3: 'THREE', 4: 'FOUR', 5: 'FIVE', 6: 'SIX' }[levelNumberOf(levelSection.level)] || text(levelSection.level) || '—').toUpperCase();
    pdf.setFontSize(12);
    pdf.text(`YEAR ${levelYearWord(levelSection)}, ${period.termWord === 'TRIMESTER' ? 'TRIMESTER' : `${period.termWord} SEMESTER`} LEVEL ${levelWord} EXAMINATION RESULTS`, center, 78, { align: 'center', maxWidth: contentWidth });
    pdf.setFontSize(14);
    pdf.text((senateOutcomeLabels[outcomeKey] || outcomeKey).toUpperCase(), center, 91, { align: 'center' });
    pdf.setLineWidth(0.25);
    pdf.line(center - 20, 93, center + 20, 93);

    const candidateCount = programmes.reduce((total, programme) => total + programme.students.length, 0);
    const periodNarrative = `YEAR ${levelYearWord(levelSection)}, ${period.termWord === 'TRIMESTER' ? 'TRIMESTER' : `${period.termWord} SEMESTER`}`;
    const candidateLabel = candidateCount === 1 ? 'candidate' : 'candidates';
    // Use the Board's standard wording for below-competency candidates while
    // keeping the recorded outcome categories visible for abscondment,
    // deregistration, withholding and deferral cases.
    const standardFailureSegments = reason => [
      { text: 'The following ' },
      { text: `${numberToWords(candidateCount)} (${candidateCount})`, bold: true },
      { text: ` ${candidateLabel} failed to satisfy the Board of Examiners of the ` },
      { text: senateInstituteLabel.toUpperCase(), bold: true },
      { text: ' in the ' },
      { text: periodNarrative, bold: true },
      { text: ` Examinations due to ${reason}.` },
    ];
    const retakeSegments = [
      ...standardFailureSegments('the reason indicated'),
      { text: ` The Institute Board of Examiners therefore recommends that ${candidateCount === 1 ? 'the candidate' : 'the candidates'} retake the unit when next offered.` },
    ];
    const narrativeSegments = ['retake_one', 'not_yet_competent'].includes(outcomeKey)
      ? retakeSegments
      : outcomeKey === 'absconded'
        ? standardFailureSegments('abscondment')
        : outcomeKey === 'deregistration'
          ? [
            ...standardFailureSegments('repeated abscondment'),
            { text: ` The Institute Board of Examiners therefore recommends that ${candidateCount === 1 ? 'the candidate be' : 'the candidates be'} deregistered as per regulations.` },
          ]
          : outcomeKey === 'withhold'
            ? [
              ...standardFailureSegments('the reason indicated'),
              { text: ` The Institute Board of Examiners therefore recommends that ${candidateCount === 1 ? 'the candidate\'s result be' : 'the candidates\' results be'} withheld pending resolution of the case by the relevant University Office.` },
            ]
            : outcomeKey === 'defer'
              ? [
                { text: 'The following ' },
                { text: `${numberToWords(candidateCount)} (${candidateCount})`, bold: true },
                { text: ` ${candidateLabel} formally deferred admission in the ` },
                { text: senateInstituteLabel.toUpperCase(), bold: true },
                { text: ` for the ${periodNarrative}. The Institute Board of Examiners therefore recommends that ${candidateCount === 1 ? 'the candidate be' : 'the candidates be'} admitted when the programme is next offered.` },
              ]
              : standardFailureSegments('the reason indicated');
    pdf.setFont('times', 'normal');
    pdf.setFontSize(standardFontSize);
    y = drawRichWrapped(pdf, narrativeSegments, margin, 102, contentWidth, standardLineHeight);
    y += 5;
    let serial = 1;
    programmes.forEach(programme => {
      ensureSpace(20);
      pdf.setFont('times', 'bold');
      pdf.setFontSize(10);
      pdf.text(programme.name.toUpperCase(), margin, y);
      drawListRule(margin, y + 1.5, pageWidth - margin, y + 1.5);
      y += 6;
      pdf.setFontSize(9.2);
      pdf.text('S/NO.', margin + 2, y);
      pdf.text('REG. NO.', margin + 20, y);
      pdf.text('NAME', margin + 67, y);
      pdf.text('UNIT CODE', margin + 95, y);
      pdf.text('REASON', margin + 136, y);
      drawListRule(margin, y + 2, pageWidth - margin, y + 2);
      y += 5;
      programme.students.forEach(student => {
        const failedUnits = [...(student.marks?.values?.() || [])]
          .filter(mark => String(getSummativeCompetency(mark).remark || '').toUpperCase().startsWith('NYC'))
          .map(mark => text(mark?.unit_code))
          .filter(Boolean);
        const unitCodes = failedUnits.length ? [...new Set(failedUnits)].join(', ') : '—';
        const reason = student.senateOutcome.issues.join('; ') || (outcomeKey === 'retake_one' ? 'INCOMPLETE EXAMINATIONS RESULTS' : 'NOT YET COMPETENT');
        const nameLines = pdf.splitTextToSize(text(student.name) || 'Unknown student', 38);
        const unitLines = pdf.splitTextToSize(unitCodes, 38);
        const reasonLines = pdf.splitTextToSize(reason, 40);
        const lineCount = Math.max(nameLines.length, unitLines.length, reasonLines.length, 1);
        // Leave enough vertical padding for wrapped names, unit codes and
        // reasons so the approval text cannot collide with the final row.
        const rowHeight = Math.max(6, (lineCount * listLineHeight) + listRowPadding);
        if (y + rowHeight > pageHeight - footerHeight - 25) {
          newPage();
          y = margin;
        }
        pdf.setFont('times', 'normal');
        pdf.setFontSize(9.2);
        pdf.text(`${serial}.`, margin + 7, y + listLineHeight, { align: 'right' });
        pdf.text(text(student.admissionNumber) || '—', margin + 20, y + listLineHeight);
        pdf.text(nameLines, margin + 67, y + listLineHeight);
        pdf.text(unitLines, margin + 95, y + listLineHeight);
        pdf.text(reasonLines, margin + 136, y + listLineHeight);
        serial += 1;
        y += rowHeight;
      });
      // Keep a single separator for the complete programme list. Candidate
      // rows themselves are intentionally not boxed or underlined.
      drawListRule(margin, y, pageWidth - margin, y);
      y += 3;
    });

    const boardApprovalText = `APPROVED BY THE BOARD OF EXAMINERS MEETING, ${documentFacultyLabel} HELD ON ${formatApprovalDate(levelSection)}.`.toUpperCase();
    const boardApprovalLineCount = Math.max(1, pdf.splitTextToSize(boardApprovalText, contentWidth).length);
    const boardApprovalHeight = boardApprovalLineCount * standardLineHeight;
    // A wrapped table row needs a visible gap before the approval statement;
    // reserve the entire Board + Senate signature area before drawing it.
    y += 6;
    ensureSpace(boardApprovalHeight + 7 + 18 + 18 + 25 + 18);
    pdf.setFont('times', 'bold');
    pdf.setFontSize(standardFontSize);
    y = drawWrapped(pdf, boardApprovalText, margin, y, contentWidth, standardLineHeight);
    y += 7;
    ensureSpace(18);
    drawSignatureBlock(margin + 6, deanName, deanRole);
    y += 18;
    ensureSpace(25);
    pdf.setFont('times', 'normal');
    pdf.setFontSize(standardFontSize);
    pdf.text('APPROVED BY THE SENATE', margin, y + 2);
    y += 13;
    ensureSpace(18);
    drawSignatureBlock(margin + 6, senateApproverName || 'SENATE APPROVER', senateApproverRole);
  };

  // Build the section order from the actual levels represented in the faculty
  // results. This prevents a hard-coded "Level Three" heading when the lowest
  // programme offered is Level Four, Five, or another configured level.
  const levelSections = new Map();
  sortedGroups.forEach(group => {
    const key = text(group.programLevel) || '—';
    const section = levelSections.get(key) || { level: group.programLevel, groups: [] };
    section.groups.push(group);
    levelSections.set(key, section);
  });
  const orderedLevelSections = [...levelSections.values()].sort((a, b) => {
    const order = levelNumberOf(a.level) - levelNumberOf(b.level);
    return order || text(a.level).localeCompare(text(b.level));
  });

  const liveAwardSections = orderedLevelSections
    .map(levelSection => ({ ...levelSection, programmes: levelAwardProgrammes(levelSection) }))
    .filter(levelSection => levelSection.programmes.length);
  // Temporary verification fallback: when no approved candidate currently
  // qualifies for an award, render the clearly labelled demo cohort so the
  // Senate award-list layout can be reviewed before live graduates exist.
  const demoAwardSections = ENABLE_DEMO_AWARD_DATA ? buildDemoAwardSections() : [];
  // Keep the temporary verification cohort visible even when one or more
  // live candidates already qualify, so every level/programme/category can
  // be reviewed before the hard-coded data is removed.
  const awardSectionsToRender = [...liveAwardSections, ...demoAwardSections]
    .sort((a, b) => {
      const levelOrder = levelNumberOf(a.level) - levelNumberOf(b.level);
      if (levelOrder) return levelOrder;
      // If live and verification sections share a level, keep live results
      // first while preserving the collection order within each source.
      return Number(Boolean(a.demo)) - Number(Boolean(b.demo));
    });

  const drawLevelDetails = (levelSection, includePurpose = false) => {
    // Keep each level's candidate lists on landscape pages after its portrait
    // section title so tables remain readable without shrinking the type.
    addLandscapeContentPage();
    if (includePurpose) {
      pdf.setFillColor(239, 243, 248);
      pdf.setDrawColor(190, 200, 212);
      pdf.rect(margin, y, contentWidth, 35, 'FD');
      pdf.setFont('times', 'bold');
      pdf.setFontSize(standardFontSize);
      pdf.text('Purpose and scope', margin + 5, y + 7);
      pdf.setFont('times', 'normal');
      pdf.setFontSize(standardFontSize);
      drawWrapped(pdf, departmentScope
        ? 'This department packet is generated from faculty-approved result records for Senate consideration and forwarding to the faculty or school. Individual marks remain in the faculty consolidated marksheets and are not reproduced here.'
        : 'This faculty or school package combines the department result sections available for the examination period. Individual marks remain in the faculty consolidated marksheets and are not reproduced here.', margin + 5, y + 12, contentWidth - 10, standardLineHeight);
      y += 44;
      if (generatedBy) {
        pdf.setFont('times', 'normal');
        pdf.setFontSize(standardFontSize);
        pdf.text(`Generated by: ${generatedBy} · ${new Date().toLocaleDateString('en-GB')}`, margin, y);
        y += standardLineHeight;
      }
    }
    drawHeading(`Level ${levelSection.level || '—'} programme outcomes`, 'The following lists support the Senate decision without displaying marks. Unit codes and recorded reasons are shown for candidates requiring action.');
    const levelGroupKeys = new Set(levelSection.groups);
    [...departmentSections.values()].forEach(section => {
      const groupsAtLevel = section.groups.filter(group => levelGroupKeys.has(group));
      if (!groupsAtLevel.length) return;
      drawHeading(`Department: ${section.departmentName}`, `${section.facultyName} · ${section.period}`);
      groupsAtLevel.forEach(group => {
        const rows = senateCandidateRows(group).map((row, index) => [
          index + 1,
          row.admissionNumber || '—',
          row.name || 'Unknown student',
          row.senateOutcome.label,
          row.senateOutcome.issues.join('; ') || '—',
        ]);
        drawHeading(`${group.programName || 'Programme'} · Level ${group.programLevel || '—'} · Module ${group.moduleNumber || '—'}`, group.period || 'Academic period not specified');
        drawTable(['SN', 'Registration No.', 'Candidate name', 'Outcome', 'Unit issues or recorded reasons'], rows, [10, 38, 49, 28, 63], { fontSize: 7.2, rowLineHeight: 3.5 });
      });
    });
  };

  orderedLevelSections.forEach(levelSection => {
    const representativeGroup = levelSection.groups[0];
    addPortraitSectionTitlePage(representativeGroup, levelSection.level);
    addPortraitLevelSummaryPage(levelSection);
    // Candidates who completed examinations but remain below competency are
    // presented before the later abscondment/deregistration outcomes.
    addPortraitOutcomePage(levelSection, 'retake_one');
    addPortraitOutcomePage(levelSection, 'not_yet_competent');
    addPortraitOutcomePage(levelSection, 'absconded');
    addPortraitOutcomePage(levelSection, 'deregistration');
    addPortraitOutcomePage(levelSection, 'withhold');
    addPortraitOutcomePage(levelSection, 'defer');
    addPortraitOutcomePage(levelSection, 'academic_leave');
    addPortraitOutcomePage(levelSection, 'special_exam');
  });

  // Award lists are deliberately the final Senate sections, after every
  // examination outcome and exception category has been presented.
  awardSectionsToRender.forEach(section => {
    addPortraitAwardTitlePage(section);
    addPortraitAwardPage(section);
  });

  drawFooter();

  const periodPart = safeFilePart(periodLabels.join('-') || 'academic-results');
  const fileName = `${departmentScope ? 'department' : 'faculty'}-senate-results-package-${periodPart}-${new Date().toISOString().slice(0, 10)}.pdf`;
  if (returnBlob) return { blob: pdf.output('blob'), fileName };
  pdf.save(fileName);
  return { fileName };
};
